import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import DateInput from "../components/DateInput";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

export default function ManagementFees() {
    const { user } = useAuth();
    const isReadOnly = user?.role === "asl_read_only_viewer";
    const [from, setFrom] = useState(monthStart());
    const [to, setTo] = useState(today());
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/dashboard/master-overview?date_from=${from}&date_to=${to}`)
            .then(({ data }) => setOverview(data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [from, to]);

    const csv = () => {
        if (!overview?.per_module) return;
        const rows = [["Module", "Code", "Gross Total", "Net Payable", "Management Fee"].join(",")];
        let totalFee = 0, totalGross = 0;
        overview.per_module.forEach((m) => {
            const fee = (m.gross_total || 0) - (m.net_payable || 0);
            totalFee += fee; totalGross += m.gross_total || 0;
            rows.push([m.module_name, m.module_code || "", m.gross_total || 0, m.net_payable || 0, fee].join(","));
        });
        rows.push(["GRAND TOTAL", "", totalGross, "", totalFee].join(","));
        const blob = new Blob([rows.join("\n")], { type: "text/csv" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
        a.download = `management-fees-${from}-to-${to}.csv`; a.click();
    };

    return (
        <section className="space-y-5" data-testid="mf-fees-page">
            <div className="flex items-end gap-3 flex-wrap">
                <div><div className="text-[10px] uppercase">From</div><DateInput value={from} onChange={setFrom} className="h-9 px-2 rounded border" style={{ borderColor: "var(--asl-border)" }} data-testid="mf-fees-from" /></div>
                <div><div className="text-[10px] uppercase">To</div><DateInput value={to} onChange={setTo} className="h-9 px-2 rounded border" style={{ borderColor: "var(--asl-border)" }} data-testid="mf-fees-to" /></div>
                {!isReadOnly && (
                    <Button variant="outline" size="sm" onClick={csv} data-testid="mf-fees-csv">
                        <Download className="h-3.5 w-3.5 mr-1" /> Download CSV
                    </Button>
                )}
            </div>
            <div className="asl-card overflow-hidden">
                {loading ? <div className="p-6 text-sm text-[color:var(--asl-muted)]">Loading…</div>
                : (
                    <table className="asl-table asl-table--zebra" data-testid="mf-fees-table">
                        <thead className="text-xs uppercase text-[color:var(--asl-muted)]">
                            <tr>
                                <th className="px-4 py-3 text-left">Module</th>
                                <th className="px-4 py-3 text-right">Gross Total</th>
                                <th className="px-4 py-3 text-right">Net Payable</th>
                                <th className="px-4 py-3 text-right asl-crimson-text">Management Fee</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(overview?.per_module || []).map((m) => {
                                const fee = (m.gross_total || 0) - (m.net_payable || 0);
                                return (
                                    <tr key={m.module_id} className="border-t" style={{ borderColor: "var(--asl-border)" }}>
                                        <td className="px-4 py-3 font-medium asl-navy-text">{m.module_name}</td>
                                        <td className="px-4 py-3 text-right mono">{fmtUSD(m.gross_total)}</td>
                                        <td className="px-4 py-3 text-right mono">{fmtUSD(m.net_payable)}</td>
                                        <td className="px-4 py-3 text-right mono asl-crimson-text font-bold">{fmtUSD(fee)}</td>
                                    </tr>
                                );
                            })}
                            <tr className="border-t-2" style={{ borderColor: "var(--asl-navy)", background: "var(--asl-navy-50)" }}>
                                <td className="px-4 py-3 font-bold asl-navy-text">GRAND TOTAL</td>
                                <td className="px-4 py-3 text-right mono font-bold">{fmtUSD((overview?.per_module || []).reduce((s, m) => s + (m.gross_total || 0), 0))}</td>
                                <td className="px-4 py-3 text-right mono font-bold">{fmtUSD((overview?.per_module || []).reduce((s, m) => s + (m.net_payable || 0), 0))}</td>
                                <td className="px-4 py-3 text-right mono font-bold asl-crimson-text">{fmtUSD((overview?.per_module || []).reduce((s, m) => s + ((m.gross_total || 0) - (m.net_payable || 0)), 0))}</td>
                            </tr>
                        </tbody>
                    </table>
                )}
            </div>
        </section>
    );
}
